import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import toLower from 'lodash/toLower';
import type { State } from '../types';
import {
  getIsCloudSiteSelected,
  getIsOrganisationSelected,
  groupByCloudSiteOrOrgName,
  isCloudSiteOrOrganisationSelected,
} from '../utils';
import {
  CartLinkedEntitlementsSchema,
  CartProductSchema,
  CloudSiteTypeEnum,
  OperationType,
  ProductStatus,
} from '../../types';
import { truthy } from '../../utils';
import { processEntity } from '../../ui/entitlement-transfer/src/utils/api';
import {
  CloudSiteOrOrgNameMap,
  CloudSiteOrOrgNameMapGroupedByBillingProfile,
} from '../../ui/billing-profile/src/types';
import { sortLinkedEntitlements } from '../utils/sort-linked-entitlements';

//this method is used to calculate apps count in right sidebar of the billing profile wrapper.
function getAppByContainerOrProductId(
  containerOrProductId: string,
  linkedEntitlements: Record<string, CartLinkedEntitlementsSchema[]>
) {
  const containerLinkedEntitlements = linkedEntitlements?.[containerOrProductId];
  // sorting linked entitlements by relationshipType and name
  const sortedLinkedEntitlements = sortLinkedEntitlements(containerLinkedEntitlements);
  const appsByContainerOrProductId = sortedLinkedEntitlements
    ?.map(({ id, operationType }) => {
      if (operationType !== OperationType.NEW) {
        return { id };
      }
    })
    ?.filter(truthy);
  return appsByContainerOrProductId ?? [];
}
function getAddonsByProductId(productId: string, addons: Record<string, CartLinkedEntitlementsSchema[]>) {
  const addonsByProductId = addons?.[productId]
    ?.map(({ id, operationType }) => {
      if (operationType !== OperationType.NEW) {
        return { id };
      }
    })
    ?.filter(truthy);
  return addonsByProductId ?? [];
}

const getInactiveCloudSiteMap = ({
  products,
  linkedEntitlements,
  addons,
}: {
  products: CartProductSchema[] | undefined;
  linkedEntitlements: Record<string, CartLinkedEntitlementsSchema[]>;
  addons: Record<string, CartLinkedEntitlementsSchema[]>;
}) => {
  if (!products || !products.length) {
    return [];
  }
  return products.map((product) => ({
    ...product,
    isChecked: false,
    linkedEntitlements: getAppByContainerOrProductId(product?.containerId || product.id, linkedEntitlements),
    addons: getAddonsByProductId(product.id, addons),
  }));
};
const getActiveCloudSiteMap = ({
  products,
  linkedEntitlements,
  selectedProductIds,
  addons,
}: {
  products: CartProductSchema[] | undefined;
  linkedEntitlements: Record<string, CartLinkedEntitlementsSchema[]>;
  addons: Record<string, CartLinkedEntitlementsSchema[]>;
  selectedProductIds: string[];
}) => {
  if (!products || !products.length || !selectedProductIds) {
    return [];
  }
  return products.map((product) => ({
    ...product,
    isChecked: selectedProductIds.includes(product.id),
    linkedEntitlements: getAppByContainerOrProductId(product?.containerId || product.id, linkedEntitlements),
    addons: getAddonsByProductId(product.id, addons),
  }));
};
const getUniqueKeysFromObjects = (...arg: Record<any, any>[]) => {
  const aggregatedObj = arg.reduce((accObj: Record<any, any>, currentObj: Record<any, any>) => {
    accObj = { ...accObj, ...currentObj };
    return accObj;
  }, {});
  return Array.from(new Set(Object.keys(aggregatedObj)));
};
/**
 * Group the products to cloud site Urls
 * @param validProducts
 * @param state
 * @param invalidProducts
 */
const groupProductsToCloudSiteOrOrgName = (
  validProducts: CartProductSchema[],
  state: State,
  invalidProducts: CartProductSchema[]
) => {
  if (!validProducts.length && !invalidProducts.length) return null;
  const linkedEntitlements = state.linkedEntitlements;
  const addons = state.addons;

  const productsGroupedByCloudSiteOrOrgName = groupBy(validProducts, groupByCloudSiteOrOrgName);
  const invalidProductsGroupedByCloudSiteOrOrgName = groupBy(invalidProducts, groupByCloudSiteOrOrgName);
  const selectedProductIds = Object.keys(state.selectedProducts);

  function mergeByCloudSiteNameOrOrgName(
    cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap,
    currentCloudSiteNameOrOrgName: keyof typeof cloudSiteOrOrgNameMap
  ) {
    const productsInCloudSite = productsGroupedByCloudSiteOrOrgName[currentCloudSiteNameOrOrgName];
    const inactiveProductsInCloudSite = invalidProductsGroupedByCloudSiteOrOrgName[currentCloudSiteNameOrOrgName];

    cloudSiteOrOrgNameMap[currentCloudSiteNameOrOrgName] = {
      products: getActiveCloudSiteMap({
        products: productsInCloudSite,
        selectedProductIds,
        linkedEntitlements,
        addons,
      }),
      inactiveProducts: getInactiveCloudSiteMap({ products: inactiveProductsInCloudSite, linkedEntitlements, addons }),
      isChecked: isCloudSiteOrOrganisationSelected(state, currentCloudSiteNameOrOrgName),
    };

    return cloudSiteOrOrgNameMap;
  }

  const allCloudSiteOrOrgNames = getUniqueKeysFromObjects(
    productsGroupedByCloudSiteOrOrgName,
    invalidProductsGroupedByCloudSiteOrOrgName
  );
  // cloudSiteMap for presentation on UI
  return allCloudSiteOrOrgNames.reduce(mergeByCloudSiteNameOrOrgName, {});
};

/**
 * this method is used to get org products grouped by org name
 * @param validProducts
 * @param state
 * @param inactiveProducts
 */
const getProductsGroupedByInvGroupIdAndOrgName = (
  validProducts: CartProductSchema[],
  state: State,
  inactiveProducts: CartProductSchema[] = []
): { cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMapGroupedByBillingProfile; showTransferForm: boolean } => {
  const { searchQuery, billingProfileIds } = state.sidebarFilter ?? {};
  const { isValid: isValidOrganisationId } = processEntity(searchQuery, true);
  const orgProducts = validProducts.filter((product) => product.organizationId);
  const inactiveOrgProducts = inactiveProducts.filter((product) => product.organizationId);
  /**
   * Here, the selected customer has no valid entitlements. Now if the entered
   * entitlement number or Organisation Id is valid, we will show the transfer form.
   */
  if (orgProducts.length === 0 && inactiveOrgProducts.length === 0 && isValidOrganisationId) {
    return {
      cloudSiteOrOrgNameMap: [],
      showTransferForm: true,
    };
  }

  const matchedOrgProducts = getFilteredProducts(orgProducts, searchQuery, billingProfileIds, true);
  const matchedOrgInactiveProducts = getFilteredProducts(inactiveOrgProducts, searchQuery, billingProfileIds, true);
  const showTransferForm =
    matchedOrgInactiveProducts.length === 0 && matchedOrgProducts.length === 0 && isValidOrganisationId;

  const productsGroupedByInvoiceGroupId = groupBy(matchedOrgProducts, 'sourceInvoiceGroupId');
  const inactiveProductsGroupedByInvoiceGroupId = groupBy(matchedOrgInactiveProducts, 'sourceInvoiceGroupId');
  const activeAndInactiveProductsBillingProfileIds = getUniqueKeysFromObjects(
    productsGroupedByInvoiceGroupId,
    inactiveProductsGroupedByInvoiceGroupId
  );
  const billingProfiles = keyBy(state.billingProfiles, 'id');

  const cloudSiteOrOrgNameMapV2: CloudSiteOrOrgNameMapGroupedByBillingProfile = activeAndInactiveProductsBillingProfileIds.map(
    (invGrpId) => {
      const productsInInvGrp = productsGroupedByInvoiceGroupId[invGrpId];
      const inactiveProductsInInvoiceGroup = inactiveProductsGroupedByInvoiceGroupId[invGrpId];

      const productsGroupedByOrgName = groupBy(productsInInvGrp, 'organisationName');
      const inactiveProductsGroupedByOrgName = groupBy(inactiveProductsInInvoiceGroup, 'organisationName');
      const selectedProductIds = Object.keys(state.selectedProducts);

      function mergeByOrganisationName(
        cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap,
        currentOrgName: keyof typeof cloudSiteOrOrgNameMap
      ) {
        const productsInOrg = productsGroupedByOrgName[currentOrgName];
        const inactiveProductsInOrg = inactiveProductsGroupedByOrgName[currentOrgName];

        cloudSiteOrOrgNameMap[currentOrgName] = {
          products: getActiveCloudSiteMap({
            products: productsInOrg,
            selectedProductIds,
            linkedEntitlements: state.linkedEntitlements,
            addons: {},
          }),
          inactiveProducts: getInactiveCloudSiteMap({
            products: inactiveProductsInOrg,
            linkedEntitlements: {},
            addons: { ...state.addons, ...state.inactiveAddons },
          }),
          isChecked: getIsOrganisationSelected(state, currentOrgName),
        };

        return cloudSiteOrOrgNameMap;
      }

      const allOrganisationNames = getUniqueKeysFromObjects(productsGroupedByOrgName, inactiveProductsGroupedByOrgName);
      return {
        billingProfileId: invGrpId,
        billingProfileName: billingProfiles[invGrpId]?.name,
        cloudSiteOrOrgNameMap: allOrganisationNames.reduce(mergeByOrganisationName, {}),
      };
    }
  );

  return {
    cloudSiteOrOrgNameMap: cloudSiteOrOrgNameMapV2,
    showTransferForm: showTransferForm,
  };
};

const getFilteredProducts = (
  products: CartProductSchema[],
  searchQuery?: string,
  billingProfileIds?: string[],
  isOrgProductSearch?: boolean
) => {
  /**
   * As the regular search and filter doubles up as a transfer search form, the flow goes like this:
   *
   * User enters a free form text.
   * If said text is NOT a valid site URL or entitlement number, or
   * if the text is a valid site URL or entitlement number but there are NO SEARCH RESULTS, we will assume the user wants to make an entitlement transfer.
   *
   * If the user entered text yields some search results from the user's own entitlements, we will continue as is and only show user's own entitlements.
   */
  const isEmptySearchQuery = !searchQuery?.length;
  const isEmptyBillingProfileIdFilter = !billingProfileIds?.length;

  if (isEmptySearchQuery && isEmptyBillingProfileIdFilter) {
    return products;
  }

  const matchesOrgSearchQuery = ({
    organisationName,
    entitlementNumber,
  }: Pick<typeof products[number], 'organisationName' | 'entitlementNumber'>) => {
    if (isEmptySearchQuery) return true;

    return (
      (organisationName && toLower(organisationName).includes(toLower(searchQuery))) ||
      toLower(entitlementNumber).includes(toLower(searchQuery))
    );
  };

  const matchesProductSearchQuery = ({
    entitlementNumber,
    cloudSite,
  }: Pick<typeof products[number], 'entitlementNumber' | 'cloudSite'>) => {
    if (isEmptySearchQuery) return true;

    return (
      toLower(entitlementNumber).includes(toLower(searchQuery)) || toLower(cloudSite).includes(toLower(searchQuery))
    );
  };

  const matchesBillingProfileFilter = ({
    sourceInvoiceGroupId,
  }: Pick<typeof products[number], 'sourceInvoiceGroupId'>) => {
    if (isEmptyBillingProfileIdFilter) return true;

    return billingProfileIds.includes(sourceInvoiceGroupId ?? '');
  };

  const matchesSearchQuery = isOrgProductSearch ? matchesOrgSearchQuery : matchesProductSearchQuery;

  return products.filter((product) => {
    return matchesSearchQuery(product) && matchesBillingProfileFilter(product);
  });
};
const getProductsGroupedByInvGroupIdAndCloudSite = ({
  validProducts,
  inactiveProducts,
  state,
  isEnterpriseAndAccessMonthlyEnabled = false,
}: {
  validProducts: CartProductSchema[];
  inactiveProducts: CartProductSchema[];
  state: State;
  isEnterpriseAndAccessMonthlyEnabled?: boolean;
}): { cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMapGroupedByBillingProfile; showTransferForm: boolean } => {
  const { searchQuery, billingProfileIds } = state.sidebarFilter ?? {};
  const { isValid: isValidEntitlementNumberOrSiteUrl } = processEntity(
    searchQuery,
    isEnterpriseAndAccessMonthlyEnabled
  );

  // Here, the selected customer has no valid entitlements. Now if the entered cloud site / entitlement number is valid, we will show the transfer form.
  if (validProducts.length === 0 && isValidEntitlementNumberOrSiteUrl) {
    return {
      cloudSiteOrOrgNameMap: [],
      showTransferForm: true,
    };
  }
  const matchedProducts = getFilteredProducts(validProducts, searchQuery, billingProfileIds);
  const matchedInactiveProducts = getFilteredProducts(inactiveProducts, searchQuery, billingProfileIds);
  const showTransferForm =
    matchedProducts.length === 0 && matchedInactiveProducts.length === 0 && isValidEntitlementNumberOrSiteUrl;
  const filteredProducts = showTransferForm
    ? isEnterpriseAndAccessMonthlyEnabled
      ? matchedProducts
      : validProducts
    : matchedProducts;
  const filteredInvalidProducts = showTransferForm
    ? isEnterpriseAndAccessMonthlyEnabled
      ? matchedInactiveProducts
      : inactiveProducts
    : matchedInactiveProducts;

  const productsGroupedByInvoiceGroupId = groupBy(filteredProducts, 'sourceInvoiceGroupId');
  const inactiveProductsGroupedByInvoiceGroupId = groupBy(filteredInvalidProducts, 'sourceInvoiceGroupId');
  const activeAndInactiveProductsBillingProfileIds = getUniqueKeysFromObjects(
    productsGroupedByInvoiceGroupId,
    inactiveProductsGroupedByInvoiceGroupId
  );
  const billingProfiles = keyBy(state.billingProfiles, 'id');
  const linkedEntitlements = state.linkedEntitlements;
  const addons = state.addons;

  const cloudSiteMapV2: CloudSiteOrOrgNameMapGroupedByBillingProfile = activeAndInactiveProductsBillingProfileIds.map(
    (invGrpId) => {
      const productsInInvGrp = productsGroupedByInvoiceGroupId[invGrpId];
      const inactiveProductsInInvGrp = inactiveProductsGroupedByInvoiceGroupId[invGrpId];

      const productsGroupedByCloudSite = groupBy(productsInInvGrp, 'cloudSite');
      const inactiveProductsGroupedByCloudSite = groupBy(inactiveProductsInInvGrp, 'cloudSite');
      const selectedProductIds = Object.keys(state.selectedProducts);

      function mergeByCloudSiteName(
        cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap,
        currentCloudSiteName: keyof typeof cloudSiteOrOrgNameMap
      ) {
        const productsInCloudSite = productsGroupedByCloudSite[currentCloudSiteName];
        const inactiveProductsInCloudSite = inactiveProductsGroupedByCloudSite[currentCloudSiteName];

        cloudSiteOrOrgNameMap[currentCloudSiteName] = {
          products: getActiveCloudSiteMap({
            products: productsInCloudSite,
            selectedProductIds: selectedProductIds,
            linkedEntitlements,
            addons,
          }),
          inactiveProducts: getInactiveCloudSiteMap({
            products: inactiveProductsInCloudSite,
            linkedEntitlements,
            addons,
          }),
          isChecked: getIsCloudSiteSelected(state, currentCloudSiteName),
        };

        return cloudSiteOrOrgNameMap;
      }

      const allCloudSiteNames = getUniqueKeysFromObjects(
        productsGroupedByCloudSite,
        inactiveProductsGroupedByCloudSite
      );

      return {
        billingProfileId: invGrpId,
        billingProfileName: billingProfiles[invGrpId]?.name,
        cloudSiteOrOrgNameMap: allCloudSiteNames.reduce(mergeByCloudSiteName, {}),
      };
    }
  );

  return {
    cloudSiteOrOrgNameMap: cloudSiteMapV2,
    showTransferForm: showTransferForm,
  };
};

export function getCloudSiteOrOrgNameMap(
  state: State,
  isEnterpriseAndAccessMonthlyEnabled = false
): {
  cloudSiteOrOrgNameMap: CloudSiteOrOrgNameMap | CloudSiteOrOrgNameMapGroupedByBillingProfile | null;
  showTransferForm?: boolean;
} | null {
  const allProducts = Object.values(state.products);
  const inactiveProducts = Object.values(state.inactiveProducts).filter(
    ({ operationType }) => operationType === OperationType.OWN
  );
  const validProducts = allProducts.filter(
    ({ productStatuses, operationType, cloudSiteType }) =>
      !productStatuses?.includes(ProductStatus.NEW) &&
      operationType === OperationType.OWN &&
      cloudSiteType !== CloudSiteTypeEnum.EXTERNAL
  );

  if (isEnterpriseAndAccessMonthlyEnabled) {
    const productsGroupedByOrgName = getProductsGroupedByInvGroupIdAndOrgName(validProducts, state, inactiveProducts);

    const nonOrgProducts = validProducts.filter((product) => !product.organizationId);
    const nonOrgInactiveProducts = inactiveProducts.filter((product) => !product.organizationId);
    const productsGroupedByCloudSite = getProductsGroupedByInvGroupIdAndCloudSite({
      validProducts: nonOrgProducts,
      state,
      isEnterpriseAndAccessMonthlyEnabled,
      inactiveProducts: nonOrgInactiveProducts,
    });
    // if only org products exists
    if (
      productsGroupedByCloudSite.cloudSiteOrOrgNameMap.length === 0 &&
      productsGroupedByOrgName.cloudSiteOrOrgNameMap.length > 0
    ) {
      return productsGroupedByOrgName;
    }
    // if only non-org products exists
    if (
      productsGroupedByCloudSite.cloudSiteOrOrgNameMap.length > 0 &&
      productsGroupedByOrgName.cloudSiteOrOrgNameMap.length === 0
    ) {
      return productsGroupedByCloudSite;
    }
    // if both org and non-org products exists
    if (
      productsGroupedByCloudSite.cloudSiteOrOrgNameMap.length > 0 &&
      productsGroupedByOrgName.cloudSiteOrOrgNameMap.length > 0
    ) {
      productsGroupedByOrgName.cloudSiteOrOrgNameMap.forEach((billingProfile) => {
        const cloudSite = productsGroupedByCloudSite.cloudSiteOrOrgNameMap.find(
          (site) => site.billingProfileId === billingProfile.billingProfileId
        );

        if (cloudSite?.billingProfileId) {
          cloudSite.cloudSiteOrOrgNameMap = {
            ...billingProfile.cloudSiteOrOrgNameMap,
            ...cloudSite.cloudSiteOrOrgNameMap,
          };
        } else {
          productsGroupedByCloudSite.cloudSiteOrOrgNameMap.push(billingProfile);
        }
      });
    }

    return {
      cloudSiteOrOrgNameMap: productsGroupedByCloudSite.cloudSiteOrOrgNameMap,
      showTransferForm: productsGroupedByOrgName.showTransferForm || productsGroupedByCloudSite.showTransferForm,
    };
  }

  return getProductsGroupedByInvGroupIdAndCloudSite({ validProducts, state, inactiveProducts });
}

export function getTransferCloudSiteOrOrgNameMap(state: State): CloudSiteOrOrgNameMap | null {
  const allProducts = Object.values(state.products);
  const allInactiveProducts = Object.values(state.inactiveProducts);
  const validProducts = allProducts.filter(({ operationType }) => operationType === OperationType.TRANSFER);
  const inactiveProducts = allInactiveProducts.filter(({ operationType }) => operationType === OperationType.TRANSFER);

  return groupProductsToCloudSiteOrOrgName(validProducts, state, inactiveProducts);
}

// fetching all products and filtering out the products which are not part of the current customer cloud site
export function getExternalCloudSiteOrOrgNameMap(state: State): CloudSiteOrOrgNameMap | null {
  const allProducts = Object.values(state.products);
  const allInactiveProducts = Object.values(state.inactiveProducts);
  const validProducts = allProducts.filter(({ cloudSiteType }) => cloudSiteType === CloudSiteTypeEnum.EXTERNAL);
  const inactiveProducts = allInactiveProducts.filter(
    ({ cloudSiteType }) => cloudSiteType === CloudSiteTypeEnum.EXTERNAL
  );

  return groupProductsToCloudSiteOrOrgName(validProducts, state, inactiveProducts);
}
